import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { computed } from "./util";

export type CartItem = {
	id: string;
	price: number;
	quantity: number;
};

type CartState = {
	items: CartItem[];
	totalPrice: () => number;
	totalItems: () => number;
};

type CartActions = {
	addToCart: (id: string, price: number, quantity?: number, maxStock?: number) => void;
	removeFromCart: (id: string) => void;
	updateQuantity: (id: string, quantity: number) => void;
	clearCart: () => void;
};

export type CartStore = CartState & CartActions;

export const createCartStore = (hubId: string) => {
	return create<CartStore>()(
		persist(
			(set, get) => ({
				items: [],

				addToCart: (id, price, quantity = 1, maxStock) => {
					set((state) => {
						const existingItem = state.items.find((item) => item.id === id);

						if (existingItem) {
							const newQuantity = existingItem.quantity + quantity;
							if (maxStock && newQuantity > maxStock) {
								return state;
							}
							return {
								items: state.items.map((item) => (item.id === id ? { ...item, quantity: newQuantity } : item)),
							};
						}

						if (maxStock && quantity > maxStock) {
							quantity = maxStock;
						}

						return {
							items: [...state.items, { id, price, quantity }],
						};
					});
				},

				removeFromCart: (id) => {
					set((state) => ({
						items: state.items.filter((item) => item.id !== id),
					}));
				},

				updateQuantity: (id, quantity) => {
					set((state) => {
						if (quantity <= 0) {
							return {
								items: state.items.filter((item) => item.id !== id),
							};
						}

						return {
							items: state.items.map((item) => (item.id === id ? { ...item, quantity: Math.min(quantity, 99) } : item)),
						};
					});
				},

				clearCart: () => {
					set({ items: [] });
				},
				totalPrice: computed(
					() => [get().items],
					(items) => items.reduce((total, item) => total + item.price * item.quantity, 0)
				),
				totalItems: computed(
					() => [get().items],
					(items) => items.reduce((total, item) => total + item.quantity, 0)
				),
			}),
			{
				name: `cart-${hubId}`,
				version: 1,
				storage: createJSONStorage(() => localStorage),
				migrate: () => {
					return {
						items: [],
					};
				},
			}
		)
	);
};
